import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';
import {
  ActivitySectionDirective,
  InteractionService,
  UserActivityClickTrackingDirective,
} from '@core';
import { InteractionPromptComponent } from '@app/components/interaction-prompt/interaction-prompt.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { transition, trigger, useAnimation } from '@angular/animations';
import { fadeIn } from 'ng-animate';
import { BrightnessService, LanguageService, NavigationService, PageService } from '@app/services';
import { CallWaiterIndicatorComponent } from '@app/components/call-waiter-indicator/call-waiter-indicator.component';
import { TextComponent } from '@app/components/text/text.component';
import { TextTranslatePipe } from '@app/pipes/text-translate.pipe';
import { ImageTranslatePipe } from '@app/pipes/image-translate.pipe';
import { ImageComponent } from '@app/components/image/image.component';
import { ColorTranslatePipe } from '@app/pipes/color-translate.pipe';
import { SizeTranslatePipe } from '@app/pipes/size-translate.pipe';
import { pageHasServiceRequest } from '@app/builder/builder-page';
import { IdleNavigationService } from '@app/services/idle-navigation';

@Component({
  selector: 'app-tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrl: './tab-bar.component.scss',
  imports: [
    RouterLink,
    ActivitySectionDirective,
    UserActivityClickTrackingDirective,
    InteractionPromptComponent,
    CallWaiterIndicatorComponent,
    TextComponent,
    TextTranslatePipe,
    ImageTranslatePipe,
    TranslatePipe,
    ImageComponent,
    ColorTranslatePipe,
    SizeTranslatePipe,
  ],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        useAnimation(fadeIn, {
          params: { timing: 0.4 },
        }),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabBarComponent {

  private readonly destroyRef = inject(DestroyRef);
  private readonly idleNavigation = inject(IdleNavigationService);
  private readonly interactionService = inject(InteractionService);
  private readonly navigationService = inject(NavigationService);
  private readonly brightnessService = inject(BrightnessService);
  private readonly languageService = inject(LanguageService);
  private readonly pageService = inject(PageService);

  private readonly itemsElementRef = viewChild<ElementRef<HTMLElement>>('itemsElement');

  public readonly currentUrl = this.idleNavigation.currentUrl;
  protected readonly enabled = this.idleNavigation.tabBarEnabled;

  protected readonly currentLanguage = toSignal(this.languageService.currentLanguage$, {
    requireSync: true,
  });

  protected readonly items = toSignal(this.navigationService.config$.pipe(
    map((navigation) => navigation.tabBar.items),
  ), { initialValue: [] });

  protected readonly withLanguageSelection = toSignal(this.languageService.config$.pipe(
    map(({ allowUserChange, supported }) => allowUserChange && supported.length > 1),
  ), { initialValue: false });

  protected readonly withBrightnessSelection = toSignal(this.brightnessService.config$.pipe(
    map(({ allowUserChange }) => allowUserChange),
  ), { initialValue: false });

  protected readonly hasTabs = computed(() => {
    const withLanguageSelection = this.withLanguageSelection();
    const withBrightnessSelection = this.withBrightnessSelection();
    const items = this.items();

    return withLanguageSelection || withBrightnessSelection || items.length > 0;
  });

  protected readonly pageIdsWithCallWaiter = toSignal(this.pageService.config$.pipe(
    map((pages) => pages
      .filter(pageHasServiceRequest)
      .map((page) => page.id),
    ),
  ));

  protected readonly hiddenWhenIdle = toSignal(this.navigationService.config$.pipe(
    map((navigation) => navigation.tabBar.hiddenWhenIdle),
  ), { initialValue: false });

  protected readonly isIdle = toSignal(this.interactionService.idle$, {
    requireSync: true,
  });

  public readonly hideTabBar = computed(() => {
    if (!this.enabled() || !this.hasTabs()) {
      return true;
    }

    if (this.hiddenWhenIdle()) {
      return this.isIdle();
    }

    return false;
  });

  constructor() {
    this.interactionService.idle$.pipe(
      distinctUntilChanged(),
      skip(1),
      filter(idle => idle),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.itemsElementRef()?.nativeElement.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    });
  }

  public isActivePage(currentUrl: string, pageId: string): boolean {
    const url = new URL(currentUrl, window.location.origin);
    const queryParams = new URLSearchParams(url.search);
    const refParam = queryParams.getAll('ref').filter((param) => param !== 'tab-bar');

    if (
      refParam.length === 0 &&
      currentUrl.startsWith(`/main/page/${ pageId }`)
    ) {
      return true;
    }

    return refParam[0] === `page:${ pageId }`;
  }

}
